import { Link, useLocation } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';

export type NavItem = {
  path: string;
  icon: LucideIcon;
  label: string;
  badge?: number | string;
  exact?: boolean;
};

export type NavGroup = {
  label: string;
  items: NavItem[];
};

export const navLinkBase =
  'flex min-h-11 md:min-h-0 items-center gap-3 px-3.5 py-2.5 rounded-[10px] transition-all duration-150 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50';
export const navLinkActive =
  'bg-[rgb(var(--accent-rgb)/0.14)] text-accentText font-semibold';
export const navLinkInactive =
  'text-glass-text-2 hover:text-glass-text hover:bg-glass-2';

export function isNavItemActive(pathname: string, item: NavItem) {
  if (item.exact) {
    return pathname === item.path || pathname === item.path.replace(/\/dashboard$/, '');
  }
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
}

export function PortalNavGroups({
  groups,
  scrollWithinSidebar = true,
}: {
  groups: NavGroup[];
  scrollWithinSidebar?: boolean;
}) {
  const location = useLocation();
  return (
    <nav
      className={`${scrollWithinSidebar ? 'min-h-0 flex-1 overflow-y-auto' : ''} space-y-3 p-3 glass-scroll`}
    >
      {groups.map((group) => (
        <div key={group.label}>
          <p className="nav-section-label">{group.label}</p>
          <div className="space-y-0.5">
            {group.items.map((item) => {
              const Icon = item.icon;
              const active = isNavItemActive(location.pathname, item);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`${navLinkBase} ${active ? navLinkActive : navLinkInactive}`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span className="flex-1 truncate">{item.label}</span>
                  {item.badge != null && item.badge !== 0 && item.badge !== '' ? (
                    <span className="text-[0.65rem] font-bold px-1.5 py-0.5 rounded-full bg-accent2/15 text-accentText">
                      {item.badge}
                    </span>
                  ) : null}
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}

export function flatNavFromGroups(groups: NavGroup[]): NavItem[] {
  return groups.flatMap((g) => g.items);
}
